import React from 'react';
import { Box, Typography } from '@mui/material';

import step1 from '../../../Assets/document/windowStep1.png'
import step2 from '../../../Assets/document/windowStep2.png'
import step3 from '../../../Assets/document/windowStep3.png'

const listItemStyle = {
  marginBottom: '0.75rem',
};

const WindowsExporterSetup = () => {
  return (
    <Box sx={{ px: { xs: 2, sm: 4 }, py: 2 }}>
      {/* Summary */}
      <Typography variant="h4" gutterBottom>
        Summary
      </Typography>
      <Typography paragraph>
        This guide explains how to install and configure the Prometheus Windows Exporter to collect Windows Server performance metrics, which can then be visualised in Grafana.
      </Typography>

      {/* Video Tutorial */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        Watch the Setup Video
      </Typography>
      <Box display="flex" justifyContent="center" my={2}>
        <iframe
          width="100%"
          height="550"
          src="https://www.youtube.com/embed/RC2Ro2kpwnE"
          title="Windows Exporter Setup Video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ borderRadius: '8px' }}
        />
      </Box>


      {/* Download and Install */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        Download and Install Windows Exporter
      </Typography>

      <Typography variant="body1" gutterBottom>
        <strong>Download the MSI Installer</strong>
      </Typography>
      <Typography paragraph>
        Download the latest release from the official GitHub page:
        <br />
        <a
          href="https://github.com/prometheus-community/windows_exporter/releases"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://github.com/prometheus-community/windows_exporter/releases
        </a>
      </Typography>
      <Typography paragraph>
        Choose the appropriate architecture: <br />
        Typically: <code>windows_exporter-&lt;version&gt;-amd64.msi</code> for most modern systems.
      </Typography>

      <Typography variant="body1" gutterBottom>
        <strong>Install the Exporter</strong>
      </Typography>
      <Typography paragraph>
        Run the downloaded <code>.msi</code> installer with administrative privileges:
        <br />
        <em>Right-click &gt; Run as Administrator</em>
      </Typography>

      <Typography paragraph>
        During the setup:
      </Typography>
      <ul style={{ paddingLeft: '1.25rem' }}>
        <li>
          <Typography variant="body1">
            Enable the Windows Firewall rule to allow inbound connections.
          </Typography>
        </li>

        <Box display="flex" justifyContent="center" my={2}>
          <img
            src={step1}
            alt="Download Windows Exporter"
            style={{ maxWidth: '100%', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}
          />
        </Box>


        <li>
          <Typography variant="body1">
            In the next section, specify the following list of collectors:
          </Typography>
          <Box component="pre" sx={{ backgroundColor: '#f4f4f4', p: 2, borderRadius: 1, mt: 1 }}>
            cpu,logical_disk,memory,net,os,service,system,textfile,cs,process,time
          </Box>
        </li>
        <li>
          <Typography variant="body1">
            <strong>Optional Collectors:</strong>
          </Typography>
          <ul style={{ paddingLeft: '1.5rem' }}>
            <li>
              <Typography variant="body1"><code>iis</code> – Enable if monitoring an IIS web server.</Typography>
            </li>
            <li>
              <Typography variant="body1"><code>mssql</code> – Enable if monitoring Microsoft SQL Server.</Typography>
            </li>

            <Box display="flex" justifyContent="center" my={2}>
              <img
                src={step2}
                alt="Download Windows Exporter"
                style={{ maxWidth: '100%', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}
              />
            </Box>

          </ul>
        </li>
      </ul>

      {/* Verify */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        Verify the Service and Metric Endpoint
      </Typography>
      <Typography paragraph>
        After successful installation:
      </Typography>
      <ul style={{ paddingLeft: '1.25rem' }}>
        <li>
          <Typography variant="body1">
            The exporter runs as a Windows service named <code>windows_exporter</code>.
          </Typography>
        </li>
        <li>
          <Typography variant="body1">
            Confirm that the service is running using PowerShell:
          </Typography>
          <Box component="pre" sx={{ backgroundColor: '#f4f4f4', p: 2, borderRadius: 1, mt: 1 }}>
            Get-Service -Name windows_exporter
          </Box>
        </li>
        <li>
          <Typography variant="body1">
            Alternatively, verify the metrics endpoint using a browser or <code>curl</code>:
          </Typography>
          <Box component="pre" sx={{ backgroundColor: '#f4f4f4', p: 2, borderRadius: 1, mt: 1 }}>
            http://&lt;server-ip&gt;:9182/metrics
          </Box>
        </li>
        <Box display="flex" justifyContent="center" my={2}>
          <img
            src={step3}
            alt="Download Windows Exporter"
            style={{ maxWidth: '100%', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}
          />
        </Box>
      </ul>

      {/* Congrats */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        🎉 Congratulations!
      </Typography>
      <Typography paragraph>
        You’ve successfully installed and configured Windows Exporter. Your Windows Server is now all set and ready to be monitored.
      </Typography>
    </Box>
  );
};

export default WindowsExporterSetup;
